import React, { useEffect, useRef, useState } from 'react'
import { ButtonLink } from '@telus-uds/components-web'
import { PriceLockup } from '@telus-uds/components-web'
import { Box } from '@telus-uds/components-web'
import { Callout } from '@telus-uds/components-web'
import ButtonLinkWrapper, { ButtonLinkWrapperProps } from '../ButtonLinkWrapper/ButtonLinkWrapper'
import { LegalTextRenderer } from '../../../utils/text/TextRenderer'
import { handleDefault, handleSelectDropdown, handlePriceToggle } from '../StickyCartV2/utils/cartUtils'
import { INavPrice, IDefaultValues, IFiveYearValues, IThreeYearValues } from '../Cart/StickyCartTypes'

import {
  StickyNavContainer,
  StickyNavInfoContainer,
  StickyNavInfo,
  NavCallout,
  StickyNavRibbon,
  XlNav,
  ButtonPadding,
} from './Styles'

export type StickyNavProps = {
  visible?: boolean
  cartActive?: boolean
  navPrice?: INavPrice
  stickyNavCalloutText?: string
  stickyNavButtonText?: string
  defaultValuesCart?: IDefaultValues
  fiveYearValues?: IFiveYearValues
  threeYearValues?: IThreeYearValues
  customButtonLinkNavProps?: ButtonLinkWrapperProps | undefined
}

const StickyCartNav = ({
  navPrice,
  stickyNavCalloutText,
  stickyNavButtonText,
  customButtonLinkNavProps,
  defaultValuesCart,
  fiveYearValues,
  threeYearValues,
}: StickyNavProps) => {
  const onOffState = useRef<boolean>(undefined)
  const yearTermCurrent = useRef<string>(undefined)
  const [priceToggleBlock, setPriceToggleBlock] = useState<boolean>(false)
  const [dropdownYearTermBlock, setDropdownYearTermBlock] = useState<boolean>(false)
  const [active, setActive] = useState<boolean>(undefined)
  const [cartActive, setCartActive] = useState<boolean>(false)
  const [yearTermValue, setYearTermValue] = useState<string>('')
  const [price, setPrice] = useState<string>(undefined)
  const [ribbonText, setRibbonText] = useState<string>('')
  const [href, setHref] = useState<string>('')

  const setDefault = () => {
    const priceToggleBlockRef = document.querySelector('#priceToggleBlock')
    const dropdownYearTermBlockRef = document.querySelector('#dropdownYearTermBlock')
    setPriceToggleBlock(!!priceToggleBlockRef)
    setDropdownYearTermBlock(!!dropdownYearTermBlockRef)
  }

  useEffect(() => {
    setDefault()
    const cartPrice = undefined
    const useMobilityCoupons = localStorage.getItem('useMobilityCoupons') === 'true' ? true : false
    const yearTerm = localStorage.getItem('yearTerm') === '5-year' ? '5-year' : '3-year'
    onOffState.current = useMobilityCoupons
    yearTermCurrent.current = yearTerm
    setActive(useMobilityCoupons)
    setYearTermValue(yearTerm)
    setCartActive(useMobilityCoupons)

    const defaultValues = handleDefault(
      'nav',
      yearTerm,
      priceToggleBlock,
      dropdownYearTermBlock,
      useMobilityCoupons,
      defaultValuesCart,
      fiveYearValues,
      threeYearValues,
      cartPrice,
      navPrice
    )

    setHref(defaultValues.url)
    setRibbonText(defaultValues.ribbonText)
    setPrice(defaultValues.price)

    const onLocalChange = (event) => {
      const eventName = event.type
      const useMobilityCouponsLocal = localStorage.getItem('useMobilityCoupons') === 'true' ? true : false
      const yearTermLocal = localStorage.getItem('yearTerm') === '5-year' ? '5-year' : '3-year'

      if (eventName === 'selectDropdownEvent') {
        const result = handleSelectDropdown(
          'nav',
          yearTermLocal,
          useMobilityCouponsLocal,
          defaultValuesCart,
          fiveYearValues,
          threeYearValues,
          cartPrice,
          navPrice
        )
        setYearTermValue(result.yearValue)
        setHref(result.url)
        setRibbonText(result.ribbonText)
        setPrice(result.price)
      } else {
        const result = handlePriceToggle(
          'nav',
          useMobilityCouponsLocal,
          yearTermLocal,
          defaultValuesCart,
          fiveYearValues,
          threeYearValues,
          cartPrice,
          dropdownYearTermBlock,
          navPrice
        )
        setActive(result.offOnState)
        setCartActive(result.offOnState)
        setHref(result.url)
        setRibbonText(result.ribbonText)
        setPrice(result.price)
      }
    }

    window.addEventListener('selectDropdownEvent', onLocalChange)
    window.addEventListener('priceToggleEvent', onLocalChange)

    return () => {
      window.removeEventListener('selectDropdownEvent', onLocalChange)
      window.removeEventListener('priceToggleEvent', onLocalChange)
    }
  }, [priceToggleBlock, dropdownYearTermBlock])

  return (
    <StickyNavContainer data-testid={'stickyCartNav-testid'}>
      <XlNav>
        <StickyNavInfoContainer>
          <Box>
            <StickyNavInfo>
              <StickyNavRibbon>
                <LegalTextRenderer>{ribbonText}</LegalTextRenderer>
              </StickyNavRibbon>
              {stickyNavCalloutText && (
                <NavCallout>
                  <Callout variant={{ rounded: true }}>
                    <LegalTextRenderer>{stickyNavCalloutText}</LegalTextRenderer>
                  </Callout>
                </NavCallout>
              )}
            </StickyNavInfo>
          </Box>
          {navPrice && (
            <PriceLockup
              data-testid={'stickyNav-pricelockUp'}
              price={price}
              size={navPrice.stickyCartPriceSize}
              rateText={navPrice.stickyCartRateText}
              signDirection={navPrice.stickyCartPriceSignDirection}
            />
          )}
        </StickyNavInfoContainer>
        <ButtonPadding style={{ paddingBottom: 5 }}>
          {customButtonLinkNavProps ? (
            <ButtonLinkWrapper
              {...customButtonLinkNavProps}
              buttonLinkProps={{
                ...customButtonLinkNavProps.buttonLinkProps,
                url: href ?? '',
              }}
            />
          ) : (
            <ButtonLink
              href={href}
              variant={{ priority: 'high', width: 'full' }}
              tokens={{
                paddingBottom: 8,
                paddingLeft: 8,
                paddingRight: 8,
                paddingTop: 8,
              }}
            >
              {stickyNavButtonText}
            </ButtonLink>
          )}
        </ButtonPadding>
      </XlNav>
    </StickyNavContainer>
  )
}

export default StickyCartNav
